import RoundedButton from 'components/RoundedButton';
import usePathnameWithNoLangCode from 'hooks/usePathnameWithNoLangCode';
import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import DurationInput from 'reusable-components/forms/ContactHostForm/DurationInput';
import { useUser } from '~/authentication/store';
import { CONTACT_HOST_SOURCES } from '~/constants';
import DateTimeInput from '~/reusable-components/forms/ContactHostForm/DateTimeInput';
import PeopleInput from '~/reusable-components/forms/ContactHostForm/PeopleInput';
import RequestInput from '~/reusable-components/forms/ContactHostForm/RequestInput';

import ErrorList from './ErrorList';
import TurnstileWidget, { useTurnstile } from './TurnstileWidget';
import usePersonalizedOfferForm from './hooks/usePersonalizedOfferForm';

const CSS = require('./RequestPersonalizedOfferForm.module.scss');

interface RequestPersonalizedOfferFormProps {
  hostId: string;
  experienceId?: string;
  source: CONTACT_HOST_SOURCES;
}

const RequestPersonalizedOfferForm: React.FC<
  RequestPersonalizedOfferFormProps
> = ({ hostId, experienceId, source }) => {
  const { isLoggedIn } = useUser();
  const [loading, setLoading] = useState(false);
  const history = useHistory();
  const pathname = usePathnameWithNoLangCode();
  const { initialFormData, handleSubmit } = usePersonalizedOfferForm({
    hostId,
    experienceId,
    source,
  });
  const [errors, setErrors] = useState<Record<string, string> | null>();
  const [turnstileToken, setTurnstileToken] = useState<string>('');
  const { isRequired: turnstileRequired } = useTurnstile();

  const handleTurnstileTokenChange = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileError = useCallback((_error: string | null) => {
    // Error is displayed by the TurnstileWidget itself
  }, []);

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    setLoading(true);
    try {
      // Block submit on web if Turnstile configured but no token yet
      if (turnstileRequired && !turnstileToken) {
        setErrors({
          turnstile: 'Please complete the security check before sending.',
        });
        return;
      }

      const { successful, errors } = await handleSubmit(
        event,
        isLoggedIn,
        history,
        pathname,
      );
      if (!successful) {
        setErrors(errors);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form
        onSubmit={onSubmit}
        className={CSS.Form}
        data-test-id="RequestPersonalizedOfferForm"
      >
        <RequestInput
          label="What makes a perfect tour for you? Include who’s joining and your top preferences!"
          placeholder="E.g., We love food tours with local specialties, street food stops, and unique dining experiences."
          defaultValue={initialFormData.request}
        />
        <PeopleInput
          label="What is your group size"
          defaultValues={{
            adults: parseInt(initialFormData.adults, 10),
            children: parseInt(initialFormData.children, 10),
          }}
        />
        <DateTimeInput
          label="Choose your preferred date and time"
          defaultValues={{
            date: initialFormData.date,
            time: initialFormData.time,
          }}
          durationComponent={
            source === CONTACT_HOST_SOURCES.HOST_PAGE && (
              <DurationInput defaultValue={3} />
            )
          }
        />
        <TurnstileWidget
          onTokenChange={handleTurnstileTokenChange}
          onError={handleTurnstileError}
        />
        <div className={CSS.SubmitButtonContainer}>
          <RoundedButton
            type="submit"
            pink
            isLoading={loading}
            edgeRounding="medium"
            className={CSS.SubmitButton}
            data-test-id={'RequestPersonalizedOfferFormButton'}
            disabled={turnstileRequired && !turnstileToken}
          >
            Send your personalization request
          </RoundedButton>
        </div>
      </form>
      <ErrorList errors={errors} />
    </>
  );
};

export default RequestPersonalizedOfferForm;
